import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Eye, Sparkles, Shield, Clover, Trophy, Users, Briefcase, ArrowRight, Phone } from 'lucide-react';

export const Services = () => {
  const services = [
    { id: 'retour-affectif', slug: 'retour-affectif', title: 'Retour Affectif', icon: Heart, description: "Retrouvez l'être aimé grâce à des rituels puissants. Réconciliation amoureuse, retour de l'être cher, renforcement des liens du couple. Spécialiste du retour affectif à Genève.", tags: ['retour affectif genève', "retour être aimé"] },
    { id: 'voyance', slug: 'voyance', title: 'Voyance & Médiumnité', icon: Eye, description: "Consultation de voyance pour éclairer votre avenir. Communication avec les esprits, prédictions précises et guidance spirituelle. Voyant médium sérieux à Genève.", tags: ['voyant genève', 'médium genève'] },
    { id: 'guerison', slug: 'guerison-spirituelle', title: 'Guérison Spirituelle', icon: Sparkles, description: "Soins énergétiques et guérison des maux physiques et spirituels. Purification de l'âme et du corps par des méthodes ancestrales africaines.", tags: ['guérisseur genève', 'soins spirituels'] },
    { id: 'protection', slug: 'desenvoutement', title: 'Désenvoûtement', icon: Shield, description: "Protection contre les forces négatives, désenvoûtement, élimination des mauvais sorts et des énergies néfastes. Libérez-vous des envoûtements.", tags: ['désenvoûtement genève', 'protection spirituelle'] },
    { id: 'chance', slug: 'chance-aux-jeux', title: 'Chance aux Jeux', icon: Clover, description: "Rituels pour attirer la chance et la fortune. Amélioration de votre destin financier et succès dans les jeux de hasard. Attirez la prospérité.", tags: ['chance aux jeux', 'rituel chance'] },
    { id: 'reussite', slug: 'reussite-professionnelle', title: 'Réussite & Succès', icon: Trophy, description: "Aide à l'obtention du permis de conduire, réussite aux examens, déblocage de situations professionnelles. Ouvrez les portes du succès.", tags: ['réussite examens', 'permis de conduire'] },
    { id: 'famille', slug: 'problemes-famille', title: 'Problèmes de Famille', icon: Users, description: "Résolution des conflits familiaux, réconciliation entre proches, harmonie au sein du foyer. Retrouvez la paix familiale.", tags: ['problèmes famille', 'conflits familiaux'] },
    { id: 'travail', slug: 'protection-spirituelle', title: 'Protection Spirituelle', icon: Briefcase, description: "Protection contre les mauvais sorts, la jalousie et les forces négatives. Protégez votre foyer, votre couple et votre carrière.", tags: ['protection spirituelle', 'mauvais oeil'] },
  ];

  return (
    <section id="services" className="py-14 bg-neutral-950 relative overflow-hidden">
      {/* Top & Bottom Gold Lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">Nos Services de Marabout à Genève</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            <span className="text-amber-400">Marabout Africain Genève</span> : Solutions à Tous Vos Problèmes
          </h2>
          <p className="text-amber-100/60 max-w-3xl mx-auto text-lg">
            <strong>Maître Diamma</strong>, grand marabout africain sérieux, vous accompagne avec des rituels puissants. Spécialiste du retour affectif rapide, voyant médium à Genève, désenvoûtement. Résultats garantis en 3 à 7 jours.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <article
                key={service.id}
                id={`service-${service.id}`}
                className="group relative bg-gradient-to-b from-neutral-900 to-neutral-950 border border-amber-500/20 rounded-2xl p-6 hover:border-amber-500/50 transition-all duration-500 hover:-translate-y-2"
                data-testid={`service-${service.id}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center mb-4 group-hover:from-amber-500/30 group-hover:to-amber-600/20 transition-all duration-300">
                  <Icon className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-amber-400 transition-colors">
                  {service.title}
                </h3>
                <p className="text-amber-100/60 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-1 mb-4">
                  {service.tags.map((tag) => (
                    <span key={tag} className="text-xs text-amber-400/60 bg-amber-500/5 px-2 py-0.5 rounded">{tag}</span>
                  ))}
                </div>
                <Link to={`/service/${service.slug}`} className="inline-flex items-center text-amber-400 text-sm font-medium hover:text-amber-300 transition-colors">
                  Consulter
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
                {/* Bottom Gold Line on Hover */}
                <div className="absolute bottom-0 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </article>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-10 p-6 bg-gradient-to-r from-amber-500/5 via-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-2xl">
          <h3 className="text-xl text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Besoin d'aide ? <span className="text-amber-400">Maître Diamma</span> est à votre écoute
          </h3>
          <p className="text-amber-200/60 mb-6 max-w-2xl mx-auto">
            Marabout sérieux et reconnu à Genève, Maître Diamma vous garantit discrétion et résultats. Première consultation gratuite.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+41767836186" className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-6 py-3 rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/25" data-testid="services-call-btn">
              <Phone className="w-5 h-5" />
              <span>+41 76 783 61 86</span>
            </a>
            <a href="#contact" className="text-amber-400 hover:text-amber-300 font-semibold transition-colors">
              Ou envoyez un message →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
